import { newId } from "../../../features/data-types"
import { Emitter, Event } from "../../../features/event-emitter"
import { Currency } from "../../../tables/types/types"
import { ProductType, useLotLoaders, useProductLoaders } from "../../products"
import { useSystemStateHooks } from "../../system"
import { useStockActions } from "../actions/stockActions"
import { useStockLoaders } from "../loaders/stockLoaders"
import { Stock } from "../state/types"

export const useStockService = () => {
    const { companyId } = useSystemStateHooks()

    const stockLoaders = useStockLoaders()
    const productLoaders = useProductLoaders()
    const lotLoaders = useLotLoaders()

    const { saveStock } = useStockActions()

    const modifyStock = async (
        productId: string,
        branchId: string,
        lotId?: string,
        availableUnitsDiff = 0,
        reservedUnitsDiff = 0
    ) => {
        const { loadProductStock } = stockLoaders()
        const loadedStock = await loadProductStock(productId, branchId, lotId, true)!

        let stock: Stock
        if (loadedStock) {
            stock = {
                ...loadedStock,
                availableUnits: loadedStock.availableUnits + availableUnitsDiff,
                reservedUnits: loadedStock.reservedUnits + reservedUnitsDiff
            }
        } else {
            stock = {
                companyId: companyId!,
                id: newId(),
                productId,
                lotId: lotId!,
                availableUnits: availableUnitsDiff,
                reservedUnits: reservedUnitsDiff,
                currency: Currency.ARS,
                branchId
            }
        }
        
        const savedStock = await saveStock(stock)
        Emitter.emit(Event.UPDATE_STOCK, savedStock)

        return savedStock
    }

    const increaseStock = async (
        productId: string,
        branchId: string,
        quantity: number,
        lotId?: string
    ) => {
        if (quantity > 0) {
            return modifyStock(productId, branchId, lotId, quantity, 0)
        }
    }

    const decreaseStock = async (
        productId: string, 
        branchId: string,
        quantity: number,
        lotId?: string
    ) => {
        if (quantity > 0) {
            return modifyStock(productId, branchId, lotId, -quantity, 0)
        }
    }

    const reserveStock = async (
        productId: string,
        branchId: string,
        quantity: number,
        lotId?: string
    ) => {
        if (quantity > 0) {
            return modifyStock(productId, branchId, lotId, -quantity, quantity)
        }
    }
    
    const enableStock = async (
        productId: string,
        branchId: string,
        quantity: number,
        lotId?: string
    ) => {
        if (quantity > 0) {
            return modifyStock(productId, branchId, lotId, quantity, -quantity)
        }
    }

    const decreaseReservedStock = async (
        productId: string,
        branchId: string,
        quantity: number,
        lotId?: string
    ) => {
        if (quantity > 0) {
            return modifyStock(productId, branchId, lotId, 0, -quantity)
        }
    }

    const transformStock = async (
        sourceProductId: string,
        destinationProductId: string,
        branchId: string,
        sourceQty: number,
        destinationQty: number,
        destinationLotId?: string
    ) => {
        const { loadProduct } = productLoaders()
        const sourceProduct = await loadProduct(sourceProductId)
        const isSourceQtyValid = sourceProduct?.type === ProductType.BULK || Number.isInteger(sourceQty)
        const destinationProduct = await loadProduct(destinationProductId)
        const isDestinationQtyValid = destinationProduct?.type === ProductType.BULK || Number.isInteger(destinationQty)
        
        if (isSourceQtyValid && isDestinationQtyValid) {
            const { loadProductBaseLot } = lotLoaders(companyId)
            
            const sourceBaseLot = await loadProductBaseLot(sourceProductId)
            await decreaseStock(sourceProductId, branchId, sourceQty, sourceBaseLot!.id)

            const destinationBaseLot = await loadProductBaseLot(destinationProductId)
            await increaseStock(destinationProductId, branchId, destinationQty, destinationLotId || destinationBaseLot!.id)
        }
    }

    return {
        increaseStock,
        decreaseStock,
        reserveStock,
        enableStock,
        decreaseReservedStock,
        transformStock
    }
}
